/* eslint-disable max-lines */
import {
  CamAction,
  Escalation,
  LocationAlarmCamera,
  StatusType,
  UnifiedAlarmGroup,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { useCallback, useMemo, useRef } from 'react';
import { SOPWorkflowContainer } from '../sop-workflow/SOPWorkflowContainer';
import { extractUnauthorizedFaces } from '../sop-workflow/utils';
import {
  useAlarmGroupCamera,
  useAlarmGroupStatic,
} from './alarm-provider/AlarmGroupProvider';
import { EscalationState } from './types';

interface Props {
  product: UnifiedMonitoringProduct;
  alarmGroup: UnifiedAlarmGroup;
  /** The escalation this group owns, already resolved by AlarmGroupContainer. */
  escalation?: Escalation;
  locationId: string;
  tenantId: string;
  escalationState?: EscalationState;
  onResolveEscalation?: (
    comment?: string,
    reason?: string,
    category?: string
  ) => void;
  onCreateEscalation?: (
    comment?: string,
    situation?: string,
    subSituation?: string
  ) => void;
  handleCamAction?: (
    actionType: CamAction
  ) => (comment?: string, action?: string, subAction?: string) => void;
  onCloseInvestigateModal?: () => void;
  alarmGroupStatus?: string;
  timeZone: string;
  onAlarmResolve?: (status: StatusType) => void;
  onEscalate?: (comment: string) => void;
}

export function UnifiedSOPWrapper(props: Props) {
  const {
    product,
    alarmGroup,
    escalation,
    locationId,
    tenantId,
    escalationState,
    onResolveEscalation,
    onCreateEscalation,
    handleCamAction,
    onCloseInvestigateModal,
    alarmGroupStatus,
    timeZone,
    onAlarmResolve,
    onEscalate,
  } = props;

  const resolutionCommentRef = useRef<string>('');

  // Get alarm cameras - different sources based on product
  const { alarmCameraIds } = useAlarmGroupStatic();
  const { camToDetailsMap } = useAlarmGroupCamera();

  const alarmCameras: LocationAlarmCamera[] = useMemo(() => {
    if (product === UnifiedMonitoringProduct.SCAN) {
      // For SCAN: use provider
      return alarmCameraIds.map((camId) => ({
        id: camId,
        name: camToDetailsMap[camId].cameraName,
        isTalkdownEnabled: camToDetailsMap[camId].isTalkdownEnabled,
      }));
    } else {
      // For REMOTE_GUARDING: use alarmGroup.alarms
      return alarmGroup.alarms.map((alarm) => ({
        id: alarm.cameraId,
        name: alarm.cameraName,
        liveStreamUrl: alarm.cameraLiveStreamUrl,
        isTalkdownEnabled: alarm.isTalkDownEnabled,
      }));
    }
  }, [product, alarmCameraIds, camToDetailsMap, alarmGroup.alarms]);

  const unauthorizedFaces = useMemo(() => {
    return extractUnauthorizedFaces(alarmGroup);
  }, [alarmGroup]);

  // Drives `isCurrentAlarmEscalated` in the workflow: reuses the escalation
  // AlarmGroupContainer already resolved rather than picking one again, which
  // is the only thing this changes.
  //
  // Enterprise comes out right by construction -- its scope predicate matches
  // on the anchor, so this resolves to the group being viewed. Remote guarding
  // does not: the anchor is often a sibling, so the workflow still offers
  // Quick Safe and opens PRE_ESCALATE under a banner saying otherwise. That is
  // unchanged here, not fixed; a fix has to go through `isEscalationOwnedBy`,
  // since `isEscalationInScope` is true for every group without a source group.
  const escalatedAlarmId =
    product === UnifiedMonitoringProduct.SCAN
      ? escalationState?.alarmGroupId
      : escalation?.initiatingLocationAlarmId;

  const alarmStartEpochMs = useMemo(() => {
    const startTimes = alarmGroup.alarms
      .map((a) => a.alarmStartTime)
      .filter((t): t is number => Boolean(t));
    return startTimes.length ? Math.min(...startTimes) : undefined;
  }, [alarmGroup]);

  const alarmEndEpochMs = useMemo(() => {
    const endTimes = alarmGroup.alarms
      .map((a) => a.alarmEndTime)
      .filter((t): t is number => Boolean(t));
    return endTimes.length ? Math.max(...endTimes) : undefined;
  }, [alarmGroup]);

  // Unified escalation handler
  const handleWorkflowEscalate = useCallback(
    (comment: string, situation?: string, subSituation?: string) => {
      if (product === UnifiedMonitoringProduct.SCAN) {
        if (!escalationState?.escalationId && onCreateEscalation) {
          onCreateEscalation(comment, situation, subSituation);
        }
      } else {
        onEscalate?.(comment);
      }
    },
    [product, escalationState?.escalationId, onCreateEscalation, onEscalate]
  );

  // Scan-specific quick resolve
  const onQuickResolve = useCallback(
    (comment = '', action?: string, subAction?: string) => {
      if (product === UnifiedMonitoringProduct.SCAN) {
        handleCamAction?.(CamAction.SAFE)(comment, action, subAction);
        onCloseInvestigateModal?.();
      }
    },
    [product, handleCamAction, onCloseInvestigateModal]
  );

  // Unified alarm resolve handler
  const handleAlarmResolve = useCallback(
    (status: StatusType, comment?: string) => {
      if (product === UnifiedMonitoringProduct.SCAN) {
        if (status === StatusType.RESOLVED) {
          resolutionCommentRef.current = comment ?? '';

          toast('Escalation resolved successfully');
          const resolutionComment = resolutionCommentRef.current;
          onResolveEscalation?.(resolutionComment);
          resolutionCommentRef.current = '';
        }
      } else {
        onAlarmResolve?.(status);
      }
    },
    [product, onResolveEscalation, onAlarmResolve]
  );

  return (
    <div className="dark:bg-ondark-bg-1 bg-onlight-bg-1 h-full overflow-auto">
      <SOPWorkflowContainer
        locationId={locationId}
        locationAlarmId={alarmGroup.id}
        alarmCameras={alarmCameras}
        escalatedAlarmId={escalatedAlarmId}
        onAlarmResolve={handleAlarmResolve}
        locationTenantId={tenantId}
        product={product}
        onEscalate={handleWorkflowEscalate}
        onQuickResolve={
          product === UnifiedMonitoringProduct.SCAN ? onQuickResolve : undefined
        }
        escalationId={escalationState?.escalationId}
        alarmGroupStatus={alarmGroupStatus}
        locationTimezone={timeZone}
        unauthorizedFaces={unauthorizedFaces}
        alarmStartEpochMs={alarmStartEpochMs}
        alarmEndEpochMs={alarmEndEpochMs}
      />
    </div>
  );
}

export default UnifiedSOPWrapper;
